import { useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  Conversation,
  Decision,
  DecisionAction,
  DerivedTruth,
  ExecutiveDigestContent,
} from "~/api/conversationTypes";
import {
  useCommitConversation,
  useConversationOutcomes,
  useConversationRuns,
  useConversationUsage,
  useCreateDecision,
  useCreateTruth,
  useDecisionControl,
  useFlattenConversation,
  useReopenConversation,
  useSupersedeTruth,
} from "~/api/queries";
import type { Document } from "~/api/types";
import { useApplyToDocument, writeBlockedReason } from "~/features/conversations/documentWrite";
import { digestMarkdown, replaceDigest } from "~/features/conversations/outcomes/executiveDigest";
import type { DocumentPorts } from "~/features/conversations/ports/editorPorts";
import { Button } from "~/foundation/components/Button";
import { ConfirmDialog } from "~/foundation/components/Dialog";
import { Empty } from "~/foundation/components/Empty";
import { Input } from "~/foundation/components/Input";
import { Text } from "~/foundation/components/Text";
import { cn } from "~/foundation/utils/cn";

/** The mutation hooks are untyped on the error side; a refusal still has to reach the reader. */
function errorText(error: unknown, t: (key: string) => string): string {
  return error instanceof Error ? error.message : t("Something went wrong");
}

/**
 * Which of the two overlapping state machines a control belongs to.
 *
 * A `Decision` carries BOTH `status` (`Recorded|Reversed|Superseded`, projected only by the
 * turn face) and `lifecycle` (`None|Proposed|PendingApproval|Active|Done|Dropped`, projected
 * only by `conversation-bdi`). They are not two names for one thing: `status` is the
 * crystallisation fact — this decision was taken — and `lifecycle` is the execution state of
 * whatever was bound to it. Both are shown, labelled, rather than merged into one pill that
 * would have to lie about one of them.
 */
const CONTROLS: Array<{ action: DecisionAction; label: string; danger?: boolean }> = [
  { action: "approve", label: "Approve" },
  { action: "deny", label: "Deny", danger: true },
  { action: "pause", label: "Pause" },
  { action: "resume", label: "Resume" },
  { action: "cancel", label: "Cancel", danger: true },
];

/**
 * What a conversation produced (W8 §B.7 / S8): its truths, its decisions, the artifacts it
 * derived, and the commit that freezes it and writes an Executive Digest into the document.
 *
 * Two server behaviours shape this panel more than any design choice:
 *
 *  - **`pause`/`resume`/`cancel` answer 200 unconditionally**, even for a decision id that
 *    does not exist (W8-2). So no control here reports success from a status code: every one
 *    invalidates and the panel re-reads, and the row's own `lifecycle` is the answer.
 *  - **A truth is append-only.** Superseding one produces a replacement and marks the
 *    original; superseding it twice is a 400 with the server's own sentence, which is shown.
 */
export function OutcomesPanel({
  conversation,
  document,
  ports,
  canUpdate,
}: {
  conversation: Conversation;
  document: Document;
  ports: DocumentPorts;
  canUpdate: boolean;
}) {
  const { t } = useTranslation();
  const outcomes = useConversationOutcomes(conversation.id);
  const runs = useConversationRuns(conversation.id);
  const usage = useConversationUsage(conversation.id);
  const createTruth = useCreateTruth();
  const supersede = useSupersedeTruth();
  const createDecision = useCreateDecision();
  const control = useDecisionControl();
  const commit = useCommitConversation();
  const reopen = useReopenConversation();
  const flatten = useFlattenConversation();
  const { apply } = useApplyToDocument();

  const [claim, setClaim] = useState("");
  const [statement, setStatement] = useState("");
  const [commitOpen, setCommitOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const committed = conversation.isCommitted;
  const blocked = writeBlockedReason(document, ports, t);
  /**
   * Provenance is required server-side: `400 "A DerivedTruth requires provenance: at least one
   * justifying message id."` The last turn is the honest default — a claim recorded with no
   * justification is exactly what this ledger exists to prevent.
   */
  const provenance = conversation.messages
    .filter((message) => message.messageType === "text")
    .slice(-1)
    .map((message) => message.id);

  const writeDigest = async (content: ExecutiveDigestContent) => {
    const markdown = digestMarkdown(content, {
      heading: t("Executive Digest"),
      summary: t("Executive Summary"),
      truths: t("Derived Truths"),
      decisions: t("Decisions"),
      risks: t("Risks / Unknowns"),
      nextActions: t("Next Actions"),
      references: t("References"),
      noSummary: t("No summary yet"),
      none: t("None noted."),
      defineNext: t("Define next actions"),
      lens: t("Conversation lens"),
      owner: t("Owner"),
      due: t("Due"),
      followUp: t("Follow up"),
      tbd: t("TBD"),
    });
    // Committing twice must leave ONE digest. The whole-body replace is the only form that can
    // promise that on both write paths, so the digest is always applied as a document replace.
    const body = replaceDigest(document.text, markdown, t("Executive Digest"));
    const outcome = await apply({
      document,
      markdown: body,
      mode: "replace_document",
      ports,
      agent: t("Executive Digest"),
    });
    setNotice(outcome.ok ? t("Executive Digest inserted") : outcome.reason);
  };

  return (
    <div className="scrollbar-thin flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
      {/* ── Commit ────────────────────────────────────────────────────────── */}
      <section className="flex flex-col gap-1">
        {committed ? (
          <>
            <Text variant="small" className="text-text-secondary">
              {t("This conversation is committed. Reopen to continue.")}
            </Text>
            <Button
              size="sm"
              className="self-start"
              disabled={!canUpdate || reopen.isPending}
              onClick={() =>
                reopen.mutate(conversation.id, {
                  onSuccess: () => setNotice(t("Conversation reopened")),
                })
              }
            >
              {t("Reopen conversation")}
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            variant="primary"
            className="self-start"
            disabled={!canUpdate || commit.isPending}
            onClick={() => setCommitOpen(true)}
          >
            {commit.isPending ? t("Committing…") : t("Commit conversation")}
          </Button>
        )}
        {blocked ? (
          <Text variant="small" className="text-text-tertiary">
            {t("The digest cannot be written into this document. {{reason}}", { reason: blocked })}
          </Text>
        ) : null}
        <Button
          size="sm"
          variant="ghost"
          className="self-start"
          disabled={!canUpdate || flatten.isPending || Boolean(blocked)}
          onClick={() =>
            flatten.mutate(
              { id: conversation.id },
              {
                onSuccess: async (result) => {
                  const outcome = await apply({
                    document,
                    markdown: result.content,
                    mode: "append",
                    ports,
                    agent: t("Conversation"),
                  });
                  setNotice(outcome.ok ? t("Inserted into the document") : outcome.reason);
                },
              },
            )
          }
        >
          {flatten.isPending ? t("Inserting…") : t("Insert a summary into the document")}
        </Button>
      </section>

      {/* ── Truths ────────────────────────────────────────────────────────── */}
      <section className="flex flex-col gap-1.5 border-t border-divider pt-2">
        <Text variant="small" className="font-medium text-text">
          {t("Derived truths")}
        </Text>
        {(outcomes.data?.derivedTruths ?? []).length === 0 ? (
          <Text variant="small" className="text-text-tertiary">
            {t("Nothing has been claimed as settled yet.")}
          </Text>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {(outcomes.data?.derivedTruths ?? []).map((truth) => (
              <TruthRow
                key={truth.id}
                truth={truth}
                canUpdate={canUpdate}
                onSupersede={(claimText) =>
                  supersede.mutate(
                    {
                      id: conversation.id,
                      truthId: truth.id,
                      draft: { claim: claimText, messageIds: truth.provenance },
                    },
                    { onError: (error) => setNotice(errorText(error, t)) },
                  )
                }
              />
            ))}
          </ul>
        )}
        <div className="flex gap-1">
          <Input
            value={claim}
            fullWidth
            label={t("Record a truth")}
            placeholder={t("What has this conversation settled?")}
            onChange={(event) => setClaim(event.target.value)}
          />
        </div>
        <Button
          size="sm"
          className="self-start"
          disabled={!canUpdate || claim.trim().length === 0 || provenance.length === 0}
          onClick={() =>
            createTruth.mutate(
              { id: conversation.id, draft: { claim: claim.trim(), messageIds: provenance } },
              {
                onSuccess: () => setClaim(""),
                onError: (error) => setNotice(errorText(error, t)),
              },
            )
          }
        >
          {t("Record")}
        </Button>
        {provenance.length === 0 ? (
          <Text variant="small" className="text-text-tertiary">
            {t("A truth needs at least one message to justify it — say something first.")}
          </Text>
        ) : null}
      </section>

      {/* ── Decisions ─────────────────────────────────────────────────────── */}
      <section className="flex flex-col gap-1.5 border-t border-divider pt-2">
        <Text variant="small" className="font-medium text-text">
          {t("Decisions")}
        </Text>
        {(outcomes.data?.decisions ?? []).length === 0 ? (
          <Text variant="small" className="text-text-tertiary">
            {t("No decisions recorded.")}
          </Text>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {(outcomes.data?.decisions ?? []).map((decision) => (
              <DecisionRow
                key={decision.id}
                decision={decision}
                canUpdate={canUpdate}
                busy={control.isPending}
                onControl={(action) =>
                  control.mutate(
                    { id: conversation.id, decisionId: decision.id, action },
                    {
                      // Never a success toast from the status alone: pause/resume/cancel answer
                      // 200 for an id that does not exist (W8-2). The re-read is the answer.
                      onSuccess: (result) =>
                        setNotice(
                          result.lifecycle
                            ? t("Now {{lifecycle}}", { lifecycle: result.lifecycle })
                            : t("Sent — re-reading the decision to confirm."),
                        ),
                      onError: (error) => setNotice(errorText(error, t)),
                    },
                  )
                }
              />
            ))}
          </ul>
        )}
        <Input
          value={statement}
          fullWidth
          label={t("Record a decision")}
          placeholder={t("What are we doing?")}
          onChange={(event) => setStatement(event.target.value)}
        />
        <Button
          size="sm"
          className="self-start"
          disabled={!canUpdate || statement.trim().length === 0 || provenance.length === 0}
          onClick={() =>
            createDecision.mutate(
              {
                id: conversation.id,
                draft: { statement: statement.trim(), messageIds: provenance },
              },
              {
                onSuccess: () => setStatement(""),
                onError: (error) => setNotice(errorText(error, t)),
              },
            )
          }
        >
          {t("Record")}
        </Button>
      </section>

      {/* ── Runs and spend ────────────────────────────────────────────────── */}
      <section className="flex flex-col gap-1 border-t border-divider pt-2">
        <Text variant="small" className="font-medium text-text">
          {t("Runs and spend")}
        </Text>
        {(runs.data ?? []).length === 0 ? (
          <Text variant="small" className="text-text-tertiary">
            {t("Nothing has been run from this conversation.")}
          </Text>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {(runs.data ?? []).map((run) => (
              <li key={run.runId} className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-small text-text">{run.runId}</span>
                <span className="text-caption text-text-tertiary">{run.status}</span>
              </li>
            ))}
          </ul>
        )}
        {usage.data ? (
          <Text variant="small" className="text-text-tertiary">
            {t("{{tokensIn}} in · {{tokensOut}} out · {{toolCalls}} tool calls", {
              tokensIn: usage.data.tokensIn,
              tokensOut: usage.data.tokensOut,
              toolCalls: usage.data.toolCalls,
            })}
          </Text>
        ) : null}
      </section>

      <div aria-live="polite" className="min-h-4 text-caption text-text-tertiary">
        {notice}
      </div>

      <ConfirmDialog
        open={commitOpen}
        onOpenChange={setCommitOpen}
        title={t("Commit this conversation?")}
        description={t(
          "Committing freezes the conversation and writes an Executive Digest section into the document. You can reopen it afterwards.",
        )}
        confirmLabel={t("Commit")}
        cancelLabel={t("Cancel")}
        closeLabel={t("Close")}
        busy={commit.isPending}
        onConfirm={() =>
          commit.mutate(
            { id: conversation.id },
            {
              onSuccess: async (result) => {
                setCommitOpen(false);
                if (result.edContent) {
                  await writeDigest(result.edContent);
                } else {
                  // The commit itself succeeded; only the digest is missing, and saying which
                  // half happened is the difference between "retry" and "check the document".
                  setNotice(t("Committed. The server returned no digest to insert."));
                }
              },
              onError: (error) => setNotice(errorText(error, t)),
            },
          )
        }
      />
    </div>
  );
}

function TruthRow({
  truth,
  canUpdate,
  onSupersede,
}: {
  truth: DerivedTruth;
  canUpdate: boolean;
  onSupersede: (claim: string) => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(truth.claim);
  const superseded = truth.supersededById !== null;

  return (
    <li className={cn("rounded-md border border-divider p-2", superseded ? "opacity-60" : null)}>
      <div className="flex items-start gap-2">
        <p className="min-w-0 flex-1 text-start text-small text-text">{truth.claim}</p>
        <span className="text-caption text-text-tertiary">
          {t("{{percent}}% confident", { percent: Math.round(truth.confidence * 100) })}
        </span>
      </div>
      {truth.confidenceBasis ? (
        <Text variant="small" className="text-text-tertiary">
          {truth.confidenceBasis}
        </Text>
      ) : null}
      {superseded ? (
        <Text variant="small" className="text-text-tertiary">
          {t("Superseded — kept, because the ledger is append-only.")}
        </Text>
      ) : editing ? (
        <div className="mt-1 flex flex-col gap-1">
          <Input
            value={draft}
            fullWidth
            aria-label={t("Replacement claim")}
            onChange={(event) => setDraft(event.target.value)}
          />
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                onSupersede(draft.trim());
                setEditing(false);
              }}
            >
              {t("Supersede")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              {t("Cancel")}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          size="sm"
          variant="ghost"
          disabled={!canUpdate}
          onClick={() => setEditing(true)}
          className="mt-1"
        >
          {t("Supersede")}
        </Button>
      )}
    </li>
  );
}

function DecisionRow({
  decision,
  canUpdate,
  busy,
  onControl,
}: {
  decision: Decision;
  canUpdate: boolean;
  busy: boolean;
  onControl: (action: DecisionAction) => void;
}) {
  const { t } = useTranslation();
  return (
    <li className="flex flex-col gap-1 rounded-md border border-divider p-2">
      <p className="text-start text-small text-text">{decision.statement}</p>
      <div className="flex flex-wrap items-center gap-2">
        {/* Two machines, two labels — merging them would have to lie about one. */}
        <span className="rounded-sm bg-bg-secondary px-1.5 py-0.5 text-caption text-text-tertiary">
          {t("Recorded as: {{status}}", { status: decision.status })}
        </span>
        {decision.lifecycle ? (
          <span className="rounded-sm bg-accent-subtle px-1.5 py-0.5 text-caption text-accent">
            {t("Execution: {{lifecycle}}", { lifecycle: decision.lifecycle })}
          </span>
        ) : null}
        {decision.owner ? (
          <span className="text-caption text-text-tertiary">{decision.owner}</span>
        ) : null}
      </div>
      {decision.rationale ? (
        <Text variant="small" className="text-text-tertiary">
          {decision.rationale}
        </Text>
      ) : null}
      <div className="flex flex-wrap gap-1">
        {CONTROLS.map((entry) => (
          <Button
            key={entry.action}
            size="sm"
            variant={entry.danger ? "ghost" : "neutral"}
            disabled={!canUpdate || busy}
            className={entry.danger ? "text-danger" : undefined}
            onClick={() => onControl(entry.action)}
          >
            {t(entry.label)}
          </Button>
        ))}
      </div>
    </li>
  );
}

/**
 * The artifacts a conversation derived (W8 §B.7 — the Outputs list).
 *
 * They arrive as `derived_artifact` MESSAGES on the turn face, which is exactly why the drawer
 * reads that face and filters client-side: the lifecycle face hides these rows, and they are
 * the record of what the conversation produced.
 */
export function OutputsList({ conversation }: { conversation: Conversation }) {
  const { t } = useTranslation();
  const artifacts = conversation.messages.filter(
    (message) => message.messageType === "derived_artifact",
  );

  if (artifacts.length === 0) {
    return <Empty message={t("No outputs yet")} />;
  }

  return (
    <ul className="flex flex-col gap-1.5">
      {artifacts.map((artifact) => {
        const status =
          artifact.metadata.raw && typeof artifact.metadata.raw.status === "string"
            ? artifact.metadata.raw.status
            : "fresh";
        return (
          <li
            key={artifact.id}
            className="flex flex-col gap-1 rounded-md border border-divider p-2"
          >
            <header className="flex items-center gap-2">
              <Text variant="small" className="min-w-0 flex-1 font-medium text-text">
                {artifact.content || t("Output")}
              </Text>
              <span
                className={cn(
                  "rounded-sm px-1.5 py-0.5 text-caption",
                  status === "stale"
                    ? "bg-warning/10 text-warning"
                    : status === "failed"
                      ? "bg-danger/10 text-danger"
                      : "bg-bg-secondary text-text-tertiary",
                )}
              >
                {status === "stale"
                  ? t("Stale")
                  : status === "failed"
                    ? t("Failed")
                    : status === "regenerating"
                      ? t("Regenerating")
                      : t("Fresh")}
              </span>
            </header>
            {artifact.flags.isStale ? (
              <Text variant="small" className="text-text-tertiary">
                {t("An upstream message changed after this was produced.")}
              </Text>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
